/**
 * Floating Bug Report Widget
 *
 * A floating button in the bottom-right corner that lets users report bugs.
 * Captures a screenshot of the current view using html2canvas and allows
 * the user to provide a title, description, and priority.
 */

import React, { useState } from 'react';
import { Bug, X, Send, Camera, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { adminApi } from '../lib/api';

const BugReportWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      // Temporarily hide the widget
      const widget = document.getElementById('bug-report-widget');
      if (widget) widget.style.display = 'none';

      const canvas = await html2canvas(document.body, {
        scale: 0.5, // Half resolution to keep size manageable
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      // Restore widget
      if (widget) widget.style.display = '';

      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      setScreenshot(dataUrl);
    } catch (e) {
      console.error('Screenshot capture failed:', e);
    } finally {
      setCapturing(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await adminApi.submitBug({
        title,
        description,
        priority,
        page: window.location.pathname,
        screenshot: screenshot || '',
      });
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setTitle('');
        setDescription('');
        setScreenshot(null);
        setPriority('medium');
      }, 2000);
    } catch {
      // swallow — bug report failures should never crash the UI
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          setIsOpen(true);
          captureScreenshot();
        }}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-rose-500 text-white shadow-lg hover:bg-rose-600 transition-all flex items-center justify-center group"
        title="Report a bug"
      >
        <Bug className="w-5 h-5" />
        <span className="absolute -top-8 right-0 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Report a Bug
        </span>
      </button>
    );
  }

  return (
    <div
      id="bug-report-widget"
      className="fixed bottom-6 right-6 z-50 w-96 bg-background border rounded-lg shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-rose-500" />
          <span className="font-semibold text-sm">Report a Bug</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {submitted ? (
        <div className="p-6 text-center">
          <div className="text-emerald-500 text-lg font-semibold">Bug Report Submitted</div>
          <p className="text-sm text-muted-foreground mt-1">Thank you! We'll look into this.</p>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {/* Screenshot Preview */}
          {capturing ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Capturing screenshot...
            </div>
          ) : screenshot ? (
            <div className="relative">
              <img src={screenshot} alt="Screenshot" className="w-full rounded border" />
              <button
                onClick={captureScreenshot}
                className="absolute top-1 right-1 bg-background/80 rounded p-1 text-xs hover:bg-background flex items-center gap-1"
              >
                <Camera className="w-3 h-3" /> Retake
              </button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={captureScreenshot}
              className="w-full text-xs gap-1"
            >
              <Camera className="w-3 h-3" /> Capture Screenshot
            </Button>
          )}

          {/* Title */}
          <div>
            <Label className="text-xs">Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue"
              className="h-8 text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs">Details</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Steps to reproduce, expected vs actual behavior..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] resize-none"
            />
          </div>

          {/* Priority */}
          <div>
            <Label className="text-xs">Priority</Label>
            <div className="flex gap-2 mt-1">
              {['low', 'medium', 'high', 'critical'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  className={`px-2 py-1 rounded text-xs capitalize ${
                    priority === p
                      ? p === 'critical'
                        ? 'bg-rose-500 text-white'
                        : p === 'high'
                          ? 'bg-amber-500 text-white'
                          : p === 'medium'
                            ? 'bg-blue-500 text-white'
                            : 'bg-muted text-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <Button
            onClick={submit}
            disabled={!title.trim() || submitting}
            className="w-full gap-1"
            size="sm"
          >
            {submitting ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            {submitting ? 'Submitting...' : 'Submit Bug Report'}
          </Button>

          <p className="text-[10px] text-muted-foreground text-center">
            Page: {window.location.pathname} · Screenshot included
          </p>
        </div>
      )}
    </div>
  );
};

export default BugReportWidget;
